import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase, apiFetch } from "@/lib/supabase";
import {
  LayoutDashboard, Users, CalendarDays, ListChecks, ClipboardList,
  Users2, Bell, BarChart3, CalendarClock, Settings as SettingsIcon, LogOut, Menu, X, ChevronDown, Check,
  DollarSign, TrendingUp, ClipboardCheck, FileText,
} from "lucide-react";
import Logo from "@/components/Logo";

/**
 * ---------- SIDEBAR THEME TOKEN ----------
 * Single source of truth for the sidebar palette. To rebrand the sidebar,
 * update these six hex values only. Do NOT hardcode sidebar colors elsewhere.
 *
 *   bg           #334E68   Primary Slate Blue background
 *   border       #3F5E7B   Medium slate — borders, hover surfaces
 *   hoverDeep    #4A6B87   Deeper slate — org-switcher hover
 *   subtitleDim  #B4C4D4   Slate tint — subtitle / secondary text on dark
 *   subtitle     #D2DBE4   Brighter slate tint — org-selector label
 *   sectionLabel #8A9BAB   Muted slate — Section captions (Main / Insights)
 *
 * Text + accent (unchanged):
 *   #FAF8F5 (cream wordmark) · #E7E5E4 (nav item text) · #E8712C (orange accent)
 * -----------------------------------------
 */

// ─── Grouped nav — Onboarding, Contracts, Forms, Documents, Calendar Sync
// live *inside* Forms & Documents and Settings → Integrations respectively;
// their routes still resolve directly so deep links keep working.
const NAV_GROUPS = [
  {
    label: "Main",
    items: [
      { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", roles: "*" },
      { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients", roles: ["org_admin", "intake_coordinator", "staff", "read_only"] },
      { to: "/app/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", roles: "*" },
      { to: "/app/scheduling", label: "Scheduling", icon: CalendarClock, testid: "nav-scheduling", roles: ["org_admin"] },
      { to: "/app/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", roles: "*" },
    ],
  },
  {
    label: "Client Services",
    items: [
      { to: "/app/intake", label: "Intake", icon: ClipboardList, testid: "nav-intake", roles: ["org_admin", "intake_coordinator"] },
      { to: "/app/service-requests", label: "Service Requests", icon: ClipboardCheck, testid: "nav-svc-reqs", roles: ["org_admin", "intake_coordinator", "staff"] },
      { to: "/app/forms-documents", label: "Forms & Documents", icon: FileText, testid: "nav-forms-docs", roles: ["org_admin", "intake_coordinator", "staff", "read_only"] },
    ],
  },
  {
    label: "Financial",
    items: [
      { to: "/app/invoices", label: "Invoices", icon: DollarSign, testid: "nav-invoices", roles: ["org_admin"] },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: ["org_admin"] },
      { to: "/app/analytics", label: "Analytics", icon: TrendingUp, testid: "nav-analytics", roles: ["org_admin"] },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/app/team", label: "Team", icon: Users2, testid: "nav-team", roles: ["org_admin"] },
      { to: "/app/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", roles: "*" },
      { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", roles: "*" },
    ],
  },
];

const allowed = (item, role) => item.roles === "*" || item.roles.includes(role);

function OrgSwitcher({ onSwitch }) {
  const { memberships, currentOrg, currentOrgId, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  if (!memberships || memberships.length === 0) return null;
  return (
    <div className="relative">
      <button data-testid="org-switcher" onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 rounded-lg bg-[#3F5E7B] hover:bg-[#4A6B87] text-left flex items-center gap-2 transition-colors">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#B4C4D4] leading-none mb-1">Organization</div>
          <div className="text-sm font-medium text-[#FAF8F5] truncate">{currentOrg?.name}</div>
        </div>
        {memberships.length > 1 && <ChevronDown size={14} className="text-[#B4C4D4] flex-shrink-0" />}
      </button>
      {open && memberships.length > 1 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#334E68] border border-[#3F5E7B] rounded-lg shadow-xl z-20 overflow-hidden">
          {memberships.map((m) => (
            <button key={m.organization_id} data-testid={`org-option-${m.organization_id}`}
              onClick={() => { switchOrg(m.organization_id); setOpen(false); onSwitch?.(); }}
              className="w-full px-3 py-2.5 text-left hover:bg-[#3F5E7B] flex items-center gap-2 text-sm">
              <span className="flex-1 truncate text-[#FAF8F5]">{m.organizations?.name}</span>
              {m.organization_id === currentOrgId && <Check size={14} className="text-[#E8712C]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { profile, currentRole, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      apiFetch("/notifications/sweep", { method: "POST" }).catch(() => {});
      const { count } = await supabase.from("notifications").select("id", { count: "exact", head: true }).eq("user_id", user.id).is("read_at", null);
      if (!cancelled) setUnread(count || 0);
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id]);

  const doSignOut = () => signOut().then(() => navigate("/login"));

  return (
    <>
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#334E68] text-[#FAF8F5] px-4 py-3 flex items-center justify-between border-b border-[#3F5E7B]">
        <div className="flex items-center gap-2.5" title="ClientDesk VA — Virtual Assistant Workspace" data-testid="brand-mobile">
          <img src="/brand/icon.svg" alt="ClientDesk VA" className="w-9 h-9 rounded-md" />
          <span className="font-display font-bold text-[17px] tracking-tight leading-none">ClientDesk VA</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-1">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <div className="md:hidden h-14" />

      <aside data-testid="sidebar"
        className={`fixed top-0 left-0 h-screen w-64 bg-[#334E68] text-[#FAF8F5] flex flex-col z-30 transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="px-4 pt-6 pb-5 border-b border-[#3F5E7B]">
          <div className="flex items-center gap-3 mb-5" data-testid="brand-desktop" title="ClientDesk VA — Virtual Assistant Workspace">
            <img src="/brand/icon.svg" alt="ClientDesk VA" className="w-14 h-14 rounded-xl flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-bold text-[21px] leading-tight tracking-tight text-[#FAF8F5]">ClientDesk&nbsp;VA</div>
              <div className="text-[12px] text-[#D2DBE4] leading-tight mt-1">Virtual Assistant Workspace</div>
            </div>
          </div>
          <OrgSwitcher onSwitch={() => setOpen(false)} />
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((i) => allowed(i, currentRole));
            if (items.length === 0) return null;
            return (
              <div key={group.label} className="mb-4">
                <div className="px-4 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#8A9BAB]">{group.label}</div>
                <div className="space-y-0.5">
                  {items.map(({ to, label, icon: Icon, end, testid }) => (
                    <NavLink key={to} to={to} end={end} data-testid={testid} onClick={() => setOpen(false)}
                      className={({ isActive }) => `flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive ? "bg-[#E8712C] text-white" : "text-[#E7E5E4] hover:bg-[#3F5E7B]"}`}>
                      <Icon size={17} strokeWidth={1.5} />
                      <span className="flex-1">{label}</span>
                      {label === "Notifications" && unread > 0 && (
                        <span data-testid="unread-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E8712C] text-white text-[10px] flex items-center justify-center font-bold">{unread > 99 ? "99+" : unread}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-[#3F5E7B]">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-[#E8712C] flex items-center justify-center text-sm font-bold">
              {profile?.full_name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="user-name">{profile?.full_name || "—"}</div>
              <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">{currentRole?.replace("_", " ")}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={doSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#E7E5E4] hover:bg-[#3F5E7B] transition-colors">
            <LogOut size={16} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
